import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Feedback } from '../components/Feedback';
import { LogoSeal } from '../components/Logo';
import { formatCurrency, formatDate } from '../lib/format';
import { PLATFORM_LABEL } from '../lib/platforms';
import {
  fetchModMessages,
  modStreamUrl,
  updateModMessageStatus,
  type DashboardMessage,
  type MessageStatus,
  type OverlayAlert,
} from '../services/api';

const SOUND_KEY = 'send-message:mod-sound';

const STATUS_LABEL: Record<MessageStatus, string> = {
  new: 'Nova',
  read: 'Lida',
  hidden: 'Oculta',
};

/** Bip curto de notificação via WebAudio (sem depender de arquivo de áudio). */
function playBeep(): void {
  try {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.25, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.4);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.4);
    oscillator.onended = () => void context.close();
  } catch {
    // Sem áudio disponível (ex.: autoplay bloqueado) — segue sem som
  }
}

/**
 * Página do MODERADOR (/mod/:token) — a integração "sem OBS".
 *
 * Mobile-first de propósito: o criador (ou um mod de confiança) abre no
 * celular/segundo monitor e acompanha TODAS as mensagens em tempo real,
 * marcando como lida/oculta durante a live. A URL é secreta e pode ser
 * revogada na dashboard (aba Integrações) sem afetar o overlay.
 */
export function ModPage() {
  const { token = '' } = useParams();

  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [messages, setMessages] = useState<DashboardMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== 'off');

  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;

  // Estado inicial (criador + últimas mensagens) e conexão SSE
  useEffect(() => {
    let cancelled = false;

    fetchModMessages(token)
      .then((data) => {
        if (cancelled) return;
        setCreatorName(data.creator.name);
        setMessages(data.messages);
      })
      .catch(() => {
        if (!cancelled) setError('Página não encontrada. Confira a URL com o criador.');
      });

    const source = new EventSource(modStreamUrl(token));
    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);

    source.addEventListener('message', (event) => {
      const alert = JSON.parse((event as MessageEvent).data) as OverlayAlert;
      const incoming: DashboardMessage = {
        id: alert.id,
        senderName: alert.senderName,
        message: alert.message,
        amountCents: alert.amountCents,
        status: 'new',
        createdAt: alert.createdAt,
        senderPlatform: alert.senderPlatform,
      };
      // Alertas de teste não são mensagens persistidas — mostramos mesmo
      // assim, marcados, para o mod saber que a integração está viva
      setMessages((current) => [incoming, ...current].slice(0, 100));
      if (soundOnRef.current) playBeep();
    });

    return () => {
      cancelled = true;
      source.close();
    };
  }, [token]);

  function toggleSound() {
    setSoundOn((current) => {
      localStorage.setItem(SOUND_KEY, current ? 'off' : 'on');
      return !current;
    });
  }

  async function changeStatus(id: string, status: MessageStatus) {
    try {
      const updated = await updateModMessageStatus(token, id, status);
      setMessages((current) => current.map((m) => (m.id === id ? updated : m)));
    } catch {
      setError('Não foi possível atualizar a mensagem. Tente de novo.');
    }
  }

  if (error && !creatorName) {
    return (
      <main className="auth-page">
        <div className="card">
          <p>{error}</p>
        </div>
      </main>
    );
  }

  if (!creatorName) {
    return <p className="page-loading">Carregando…</p>;
  }

  return (
    <div className="mod">
      <header className="mod__header">
        <div className="mod__brand">
          <LogoSeal size={28} />
          <div>
            <h1 className="mod__title">Moderação — {creatorName}</h1>
            <p className={`mod__status${connected ? ' mod__status--on' : ''}`}>
              {connected ? '● Ao vivo: recebendo mensagens' : '○ Reconectando…'}
            </p>
          </div>
        </div>
        <button className="button button--small button--ghost" type="button" onClick={toggleSound}>
          {soundOn ? '🔔 Som ligado' : '🔕 Som desligado'}
        </button>
      </header>

      <main className="mod__content">
        {error && <Feedback kind="error">{error}</Feedback>}

        {messages.length === 0 && (
          <div className="card empty-state">
            <p>Nenhuma mensagem ainda. Elas aparecem aqui na hora em que chegarem.</p>
          </div>
        )}

        <ul className="message-list">
          {messages.map((msg) => (
            <li
              key={msg.id}
              className={`card message-item message-item--${msg.status}${
                msg.id.startsWith('test-') ? ' message-item--test' : ''
              }`}
            >
              <div className="message-item__header">
                <strong className="message-item__sender">
                  {msg.senderName}
                  {msg.id.startsWith('test-') && ' (teste)'}
                  {msg.senderPlatform && (
                    <span className="platform-badge">via {PLATFORM_LABEL[msg.senderPlatform]}</span>
                  )}
                </strong>
                <span className="message-item__amount">{formatCurrency(msg.amountCents)}</span>
              </div>
              <p className="message-item__content">{msg.message}</p>
              <div className="message-item__footer">
                <span className="message-item__meta">
                  {formatDate(msg.createdAt)} · {STATUS_LABEL[msg.status]}
                </span>
                {!msg.id.startsWith('test-') && (
                  <div className="message-item__actions">
                    {msg.status !== 'read' && (
                      <button
                        className="button button--small"
                        type="button"
                        onClick={() => changeStatus(msg.id, 'read')}
                      >
                        Marcar lida
                      </button>
                    )}
                    {msg.status !== 'hidden' ? (
                      <button
                        className="button button--small button--ghost"
                        type="button"
                        onClick={() => changeStatus(msg.id, 'hidden')}
                      >
                        Ocultar
                      </button>
                    ) : (
                      <button
                        className="button button--small button--ghost"
                        type="button"
                        onClick={() => changeStatus(msg.id, 'new')}
                      >
                        Restaurar
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
